import { useEffect, useState } from 'react';
import { entriesSince } from '@nexops/shared';
import { useI18n } from '@/i18n/I18nProvider';
import { useCurrentUser } from '@/features/auth/auth.store';
import { APP_BUILD } from '@/shared/lib/app-version';
import { ChangelogDialog } from './ChangelogDialog';
import { useChangelog } from './useChangelog';

/**
 * Po aktualizacji pokazuje raz „Co nowego” (wpisy nowsze niż ostatnio potwierdzona wersja tego użytkownika).
 * Pierwsze logowanie na urządzeniu niczego nie pokazuje — zapisuje tylko bieżącą wersję, żeby nie witać nowych osób listą zmian.
 */
export function WhatsNewPrompt() {
  const { t } = useI18n();
  const user = useCurrentUser();
  const key = user ? `nexops-ack-version:${user.id}` : null;
  const [lastSeen, setLastSeen] = useState<string | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const changelog = useChangelog(!!key && !import.meta.env.DEV);

  useEffect(() => {
    if (!key) return;
    try {
      setLastSeen(localStorage.getItem(key));
    } catch {
      setLastSeen(null);
    }
  }, [key]);

  const fresh = lastSeen && changelog.data ? entriesSince(changelog.data, lastSeen) : [];

  useEffect(() => {
    if (!key || lastSeen === undefined) return;
    if (lastSeen === null) {
      try {
        localStorage.setItem(key, APP_BUILD.version);
      } catch {
        /* ignore */
      }
      return;
    }
    if (lastSeen !== APP_BUILD.version && fresh.length) setOpen(true);
    else if (lastSeen !== APP_BUILD.version && changelog.data) {
      try {
        localStorage.setItem(key, APP_BUILD.version);
      } catch {
        /* ignore */
      }
    }
  }, [key, lastSeen, fresh.length, changelog.data]);

  const close = (next: boolean) => {
    setOpen(next);
    if (!next && key) {
      try {
        localStorage.setItem(key, APP_BUILD.version);
      } catch {
        /* ignore */
      }
      setLastSeen(APP_BUILD.version);
    }
  };

  if (!open) return null;
  return <ChangelogDialog open onOpenChange={close} entries={fresh} title={t('appu.updated', { version: APP_BUILD.version })} />;
}
